import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { HiveCopySource } from 'app/model/pipeline/copysource/hive.copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { GlobalVariables } from 'app/services/globals';
import moment from 'moment-timezone';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(HiveCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        batchSize: GlobalVariables.enableHiveV2BatchSize ? '*' : undefined,
        queryTimeout: GlobalVariables.enableHiveV2QueryTimeout ? '*' : undefined
    }
})
export class HiveCopySourceAdapter extends CopySourceAdapter<HiveCopySource, Model.HiveSource> {

    protected override async _toResource(copySource: HiveCopySource): Promise<Model.HiveSource> {
        const result = await this._toTabularResource(copySource, Metamodel.modelFor(HiveCopySource).name);
        if (GlobalVariables.enableHiveV2BatchSize) {
            result.batchSize = ValueAdapter.toResource(copySource, HiveCopySource.BATCH_SIZE);
        }
        if (GlobalVariables.enableHiveV2QueryTimeout) {
            try {
                result.queryTimeout =
                    typeof copySource.queryTimeout === 'number'
                        ? moment.duration(copySource.queryTimeout, 'seconds').format('hh:mm:ss', { trim: false })
                        : ValueAdapter.toResource(copySource, HiveCopySource.QUERY_TIMEOUT);
            } catch {
                this._logError('Failed to convert queryTimeout into TimeSpan');
            }
        }
        return result;
    }

    protected override async _toModel(resource: Model.HiveSource, copyActivity: CopyActivity): Promise<HiveCopySource> {
        const result = (await this._toTabularModel(resource, copyActivity, HiveCopySource.MODEL)) as HiveCopySource;
        if (GlobalVariables.enableHiveV2BatchSize) {
            ValueAdapter.toModel(result, HiveCopySource.BATCH_SIZE, resource.batchSize);
        }
        if (GlobalVariables.enableHiveV2QueryTimeout) {
            try {
                if (resource.queryTimeout == null) {
                    result.queryTimeout = undefined;
                } else if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    result.queryTimeout = moment.duration(resource.queryTimeout).asSeconds();
                } else {
                    ValueAdapter.toModel(result, HiveCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            } catch {
                this._logError('Failed to convert queryTimeout into number');
            }
        }
        return result;
    }
}
